import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { ComponentPropsWithoutRef } from 'react';
import { ID_NEW } from '../../../../../domain/model/category/ICategoryRepository';
import { WebCategory } from '../../../../../domain/model/category/WebCategory';
import Component from '../components/js/NewsFeed';
import { ITopStates } from '../store';
import { actions, convertCacheKey, initialListState, type Action as NewsFeedAction } from '../store/newsFeed';

/**
 * 主要を除くカテゴリの container
 *
 * @param state
 */
const mapStateToProps = (state: ITopStates) => {
  const category = state.globalNavigation.currentCategory;
  const base = {
    yadsAdDsId: '90113_264668',
    experimentId: state.pageData.pageParam.vtgrpid,
    bucketId: state.pageData.pageParam.vtestid,
    category,
    ualElementId: getElementId(category),
    isMajor: false,
    userToken: state.userToken,
    isFetchingForDislike: true, // カテゴリトップはDISLIKE対象外なのでtrue固定指定
    categoryId: state.pageData.yads.categoryId
  };

  if (category === WebCategory.LOCAL) {
    // 地域用 NewsFeed
    const { currentRegion, currentPrefecture, isReflectedStorage } = state.newsFeedLocalTab;

    if (!isReflectedStorage || !currentRegion) {
      // localstorageの状態が未反映 または都道府県が未選択の場合
      return { ...initialListState, ...base, isFetchingFirst: true };
    }

    if (currentRegion.id === ID_NEW) {
      // 全国の場合
      const cacheKey = convertCacheKey(WebCategory.LOCAL, ID_NEW);
      const currentListState = state.newsFeed.stateCache[cacheKey] || initialListState;

      return {
        ...base,
        ...currentListState,
        currentSubCategory: {
          id: ID_NEW,
          name: ''
        },
        isFetchingFirst: currentListState.isFetching && currentListState.displayCount === 0
      };
    } else {
      // 全国以外
      const currentSubCategory = currentRegion.prefectures.find((prefecture, index) =>
        currentPrefecture ? prefecture.id === currentPrefecture.id : index === 0
      );

      if (!currentSubCategory) {
        // 選択中のサブカテが取得できない場合
        return {
          ...base,
          ...initialListState,
          currentSubCategory,
          isFetchingFirst: true
        };
      }
      const cacheKey = convertCacheKey(WebCategory.LOCAL, currentSubCategory.id);
      const currentListState = state.newsFeed.stateCache[cacheKey] || initialListState;
      return {
        ...base,
        ...currentListState,
        currentSubCategory,
        isFetchingFirst: currentListState.isFetching && currentListState.displayCount === 0
      };
    }
  } else {
    // 地域以外のカテゴリ
    const { tabStorage, isReflectedStorage } = state.newsFeedTab;

    if (!isReflectedStorage) {
      // localstorageの状態が未反映 または都道府県が未選択の場合
      return { ...initialListState, ...base, isFetchingFirst: true };
    }

    const selectedSubCategory: string | undefined = tabStorage ? tabStorage[category || ''] : undefined;

    const currentSubCategory = state.newsFeedTab.subCategories.find((item, index) =>
      // 現在選択中のカテゴリを選択する : 選択中のカテゴリがなければ先頭を使う
      selectedSubCategory ? item.id === selectedSubCategory : index === 0
    );

    if (!currentSubCategory) {
      // 選択中のサブカテが取得できない場合
      return {
        ...base,
        ...initialListState,
        currentSubCategory,
        isFetchingFirst: true
      };
    }

    const cacheKey = convertCacheKey(category || '', currentSubCategory.id);
    const currentListState = state.newsFeed.stateCache[cacheKey] || initialListState;

    return {
      ...base,
      ...currentListState,
      currentSubCategory,
      isFetchingFirst: currentListState.isFetching && currentListState.displayCount === 0
    };
  }
};

const mapDispatchToProps = (dispatch: ThunkDispatch<ITopStates, void, NewsFeedAction>) => ({
  /**
   * Feedを取得する
   *
   * @param category
   * @param subCategoryId
   * @param userToken
   */
  fetchFeed: (category: WebCategory, subCategoryId: string, userToken: string | null) => {
    dispatch(actions.fetchFeed(category, subCategoryId, userToken));
  },
  /**
   * Feedを追加取得する
   *
   * @param category
   * @param subCategoryId
   * @param start
   * @param userToken
   */
  fetchMoreFeed: (category: WebCategory, subCategoryId: string, start: number, userToken: string | null) => {
    dispatch(actions.fetchFeed(category, subCategoryId, userToken, start));
  },
  setIsOpenDislikePopUp: (props: {
    categoryId: WebCategory;
    subCategoryId: string;
    contentId: string;
    isOpenDislikePopUp: boolean;
  }) => {
    dispatch(actions.setIsOpenDislikePopUp(props));
  }
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type OwnProps = Omit<
  ComponentPropsWithoutRef<typeof Component>,
  keyof StateProps | keyof DispatchProps | 'fetch' | 'fetchMore' | 'setIsOpenDislikePopUp'
>;

const mergeProps = (stateProps: StateProps, dispatchProps: DispatchProps, ownProps: OwnProps) => {
  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    // 初回取得処理
    fetch: () => {
      const { category, userToken } = stateProps;
      const currentSubCategory = 'currentSubCategory' in stateProps ? stateProps.currentSubCategory : undefined;
      if (!category || !currentSubCategory) {
        return;
      }

      dispatchProps.fetchFeed(category, currentSubCategory.id, userToken.value);
    },
    // 追加読み込み処理
    fetchMore: () => {
      const { articleCount, category, userToken } = stateProps;
      const currentSubCategory = 'currentSubCategory' in stateProps ? stateProps.currentSubCategory : undefined;
      if (!category || !currentSubCategory) {
        return;
      }
      dispatchProps.fetchMoreFeed(category, currentSubCategory.id, articleCount, userToken.value);
    },
    setIsOpenDislikePopUp: (contentId: string, isOpenDislikePopUp: boolean) => {
      const { category } = stateProps;
      const currentSubCategory = 'currentSubCategory' in stateProps ? stateProps.currentSubCategory : undefined;
      if (!category || !currentSubCategory) {
        return;
      }
      dispatchProps.setIsOpenDislikePopUp({
        categoryId: category,
        subCategoryId: currentSubCategory.id,
        contentId,
        isOpenDislikePopUp
      });
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(Component);

/**
 * UALコンテンツ接触ログのElementIdを取得する
 *
 * @param category
 */
function getElementId(category?: WebCategory): string | undefined {
  if (!category) {
    return undefined;
  }
  switch (category) {
    case WebCategory.MAJOR:
      return 'first-main-timeline';
    case WebCategory.DOMESTIC:
      return 'first-domestic-timeline';
    case WebCategory.WORLD:
      return 'first-international-timeline';
    case WebCategory.BUSINESS:
      return 'first-economic-timeline';
    case WebCategory.ENTERTAINMENT:
      return 'first-entertainment-timeline';
    case WebCategory.SPORTS:
      return 'first-sports-timeline';
    case WebCategory.IT:
      return 'first-it-timeline';
    case WebCategory.SCIENCE:
      return 'first-science-timeline';
    case WebCategory.LIFE:
      return 'first-life-timeline';
    case WebCategory.LOCAL:
      return 'first-region-timeline';
  }
  return undefined;
}
